import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  RefreshCw, Calendar, Package, Pause, Play, X, Edit2, 
  Clock, MapPin, Truck, Store, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscriptions = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSub, setSelectedSub] = useState(null);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [pauseType, setPauseType] = useState('next_order');
  const [resumeDate, setResumeDate] = useState('');
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    if (!user) {
      toast.error('Please login to view subscriptions');
      navigate('/login');
      return;
    }
    fetchSubscriptions();
  }, [user, authLoading]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    if (!selectedSub) return;
    try {
      await axios.post(
        `${API}/subscriptions/${selectedSub.id}/pause`,
        { pause_type: pauseType, resume_date: pauseType === 'until_date' ? resumeDate : null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription paused');
      setShowPauseDialog(false);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to pause subscription');
    }
  };

  const handleResume = async (subId) => {
    try {
      await axios.post(
        `${API}/subscriptions/${subId}/resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription resumed');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to resume subscription');
    }
  };

  const handleCancel = async () => {
    if (!selectedSub) return;
    try {
      await axios.post(
        `${API}/subscriptions/${selectedSub.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription cancelled');
      setShowCancelDialog(false);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleUpdate = async () => {
    if (!selectedSub) return;
    try {
      await axios.patch(
        `${API}/subscriptions/${selectedSub.id}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription updated');
      setShowEditDialog(false);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getFrequencyLabel = (freq) => {
    const labels = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Every 2 Weeks', monthly: 'Monthly' };
    return labels[freq] || freq;
  };

  const getNextDelivery = (sub) => {
    const today = new Date().toISOString().split('T')[0];
    const upcoming = sub.scheduled_dates?.filter(
      d => d >= today && !sub.completed_dates?.includes(d) && !sub.skipped_dates?.includes(d)
    );
    return upcoming?.[0];
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <RefreshCw className="h-8 w-8 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-bold text-accent">My Subscriptions</h1>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading subscriptions...</div>
        ) : subscriptions.length === 0 ? (
          <Card className="p-8 text-center">
            <RefreshCw className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="text-xl font-semibold text-accent mb-2">No Subscriptions Yet</h2>
            <p className="text-muted-foreground mb-4">
              Subscribe to your favorite products and get them delivered regularly!
            </p>
            <Button onClick={() => navigate('/products')} className="bg-primary">
              Browse Products
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {subscriptions.map((sub) => (
              <Card key={sub.id} className="p-4 sm:p-6 border-l-4 border-l-primary">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Product Info */}
                  <div className="flex gap-4">
                    <div className="w-20 h-20 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Package className="h-10 w-10 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-accent">{sub.product?.name}</h3>
                        <Badge className={getStatusColor(sub.status)}>{sub.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Qty: {sub.quantity} • {getFrequencyLabel(sub.frequency)}</p>
                      <p className="text-sm text-primary font-medium">₹{sub.product?.price * sub.quantity} per delivery</p>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {sub.start_date} to {sub.end_date}
                        </span>
                        <span className="flex items-center gap-1">
                          {sub.delivery_type === 'pickup' ? <Store className="h-3 w-3" /> : <Truck className="h-3 w-3" />}
                          {sub.delivery_type === 'pickup' ? 'Pickup' : 'Delivery'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Next Delivery & Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Next Delivery */}
                    {sub.status === 'active' && getNextDelivery(sub) && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs text-green-600 font-medium">Next Delivery</p>
                        <p className="text-sm font-bold text-green-800">{getNextDelivery(sub)}</p>
                        <p className="text-xs text-green-600">4:00 PM - 6:00 PM</p>
                      </div>
                    )}

                    {sub.status === 'paused' && sub.pause_info && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-xs text-yellow-600 font-medium flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Paused
                        </p>
                        {sub.pause_info.resume_date && (
                          <p className="text-sm text-yellow-800">Resumes: {sub.pause_info.resume_date}</p>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    {sub.status === 'active' && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => { setSelectedSub(sub); setShowPauseDialog(true); }}
                        >
                          <Pause className="h-3 w-3" />
                          Pause
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => { 
                            setSelectedSub(sub); 
                            setEditData({ quantity: sub.quantity, end_date: sub.end_date });
                            setShowEditDialog(true); 
                          }}
                        >
                          <Edit2 className="h-3 w-3" />
                          Modify
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-red-600 hover:text-red-700"
                          onClick={() => { setSelectedSub(sub); setShowCancelDialog(true); }}
                        >
                          <X className="h-3 w-3" />
                          Cancel
                        </Button>
                      </div>
                    )}

                    {sub.status === 'paused' && (
                      <Button
                        size="sm"
                        className="gap-1 bg-green-600 hover:bg-green-700"
                        onClick={() => handleResume(sub.id)}
                      >
                        <Play className="h-3 w-3" />
                        Resume
                      </Button>
                    )}
                  </div>
                </div>

                {/* Delivery Schedule */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Delivery Schedule</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>Done</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>Today</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block"></span>Upcoming</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block"></span>Skipped</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {sub.scheduled_dates?.map((date) => {
                      const today = new Date().toISOString().split('T')[0];
                      const isCompleted = sub.completed_dates?.includes(date);
                      const isSkipped = sub.skipped_dates?.includes(date);
                      const isToday = date === today;
                      const isPast = date < today && !isCompleted;
                      return (
                        <span
                          key={date}
                          title={isCompleted ? 'Order generated' : isSkipped ? 'Skipped' : isToday ? 'Today!' : date}
                          className={`text-xs px-2 py-1 rounded-md font-medium transition-all ${
                            isCompleted ? 'bg-green-100 text-green-700 border border-green-200' :
                            isSkipped ? 'bg-gray-100 text-gray-400 border border-gray-200 line-through' :
                            isToday ? 'bg-amber-400 text-white border border-amber-500 ring-1 ring-amber-300 shadow-sm' :
                            isPast ? 'bg-red-50 text-red-400 border border-red-100' :
                            'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          {date.slice(5)}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {sub.completed_dates?.length || 0} of {sub.scheduled_dates?.length || 0} deliveries completed
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Pause Dialog */}
      <Dialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pause Subscription</DialogTitle>
            <DialogDescription>Choose how you want to pause this subscription</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Pause Type</Label>
              <Select value={pauseType} onValueChange={setPauseType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="next_order">Skip Next Order Only</SelectItem>
                  <SelectItem value="until_date">Pause Until Specific Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {pauseType === 'until_date' && (
              <div className="space-y-2">
                <Label>Resume Date</Label>
                <Input
                  type="date"
                  value={resumeDate}
                  onChange={(e) => setResumeDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPauseDialog(false)}>Cancel</Button>
            <Button onClick={handlePause} className="bg-yellow-600 hover:bg-yellow-700">Pause</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this subscription? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Go Back</Button>
            <Button onClick={handleCancel} variant="destructive">Yes, Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modify Subscription</DialogTitle>
            <DialogDescription>Update your subscription details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                value={editData.quantity || 1}
                onChange={(e) => setEditData({ ...editData, quantity: parseInt(e.target.value) })}
                min="1"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={editData.end_date || ''}
                onChange={(e) => setEditData({ ...editData, end_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-primary">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Subscriptions;
